import { BarChart3, Brain, Target, TrendingUp, Eye, Database, Mic, Activity } from 'lucide-react';
import type { Product } from './types';

export const intelligenceProducts: Product[] = [
  {
    id: 'business-intelligence-suite',
    title: 'Business Intelligence Suite',
    subtitle: 'Comprehensive data analytics',
    description: 'Advanced business intelligence platform with real-time dashboards, predictive analytics, and automated reporting for data-driven decision making.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Intelligence & Analytics',
    status: 'Available',
    features: [
      'Real-time dashboards',
      'Predictive analytics',
      'Automated reporting',
      'Data visualization',
      'KPI tracking',
      'Custom metrics',
      'Export capabilities',
      'Mobile access'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-blue-500 to-cyan-600',
    businessTypes: ['#analytics', '#business-intelligence', '#reporting', '#dashboards', '#data-analysis']
  },
  {
    id: 'predictive-analytics-engine',
    title: 'Predictive Analytics Engine',
    subtitle: 'AI-powered forecasting',
    description: 'Machine learning-powered predictive analytics platform for accurate forecasting, trend analysis, and business planning.',
    icon: Brain,
    image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Intelligence & Analytics',
    status: 'Available',
    features: [
      'Machine learning algorithms',
      'Trend forecasting',
      'Risk assessment',
      'Pattern recognition',
      'Scenario modeling',
      'Automated insights',
      'Real-time predictions',
      'Custom models'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-purple-500 to-indigo-600',
    businessTypes: ['#predictive-analytics', '#forecasting', '#machine-learning', '#ai', '#business-planning']
  },
  {
    id: 'market-research-platform',
    title: 'Market Research Platform',
    subtitle: 'Comprehensive market insights',
    description: 'Advanced market research platform with survey tools, competitor analysis, consumer insights, and trend tracking.',
    icon: Target,
    image: 'https://images.unsplash.com/photo-1542744094-3a31f272c490?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Intelligence & Analytics',
    status: 'Available',
    features: [
      'Survey creation tools',
      'Competitor analysis',
      'Consumer insights',
      'Trend tracking',
      'Market segmentation',
      'Brand analysis',
      'Report generation',
      'Data visualization'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-teal-600',
    businessTypes: ['#market-research', '#competitor-analysis', '#consumer-insights', '#surveys', '#trend-analysis']
  },
  {
    id: 'performance-analytics-dashboard',
    title: 'Performance Analytics Dashboard',
    subtitle: 'Real-time performance tracking',
    description: 'Comprehensive performance analytics dashboard with KPI monitoring, goal tracking, and automated alerts.',
    icon: TrendingUp,
    image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Intelligence & Analytics',
    status: 'Available',
    features: [
      'KPI monitoring',
      'Goal tracking',
      'Performance metrics',
      'Automated alerts',
      'Benchmark analysis',
      'Custom dashboards',
      'Report scheduling',
      'Mobile access'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-orange-500 to-red-600',
    businessTypes: ['#performance-analytics', '#kpi-tracking', '#goal-management', '#metrics', '#monitoring']
  },
  {
    id: 'data-visualization-studio',
    title: 'Data Visualization Studio',
    subtitle: 'Interactive data visualization',
    description: 'Professional data visualization platform with interactive charts, custom dashboards, and advanced visual analytics.',
    icon: Eye,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Intelligence & Analytics',
    status: 'Available',
    features: [
      'Interactive charts',
      'Custom dashboards',
      'Visual analytics',
      'Data storytelling',
      'Export options',
      'Real-time updates',
      'Mobile optimization',
      'Collaboration tools'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-teal-500 to-blue-600',
    businessTypes: ['#data-visualization', '#charts', '#dashboards', '#visual-analytics', '#reporting']
  },
  {
    id: 'customer-experience-analytics',
    title: 'Customer Experience Analytics',
    subtitle: 'Advanced customer insights',
    description: 'Comprehensive customer analytics platform with 3D visualization, behavioral analysis, and predictive customer intelligence.',
    icon: Database,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Intelligence & Analytics',
    status: 'Available',
    features: [
      '3D data visualization',
      'Behavioral analytics',
      'Customer journey mapping',
      'Predictive intelligence',
      'Real-time dashboards',
      'Sentiment analysis',
      'Conversion optimization',
      'Custom reporting tools'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-cyan-500 to-blue-600',
    businessTypes: ['#analytics', '#customer-experience', '#visualization', '#insights', '#optimization']
  },
  {
    id: 'ai-executive-intelligence',
    title: 'AI-Powered Executive Intelligence Platform',
    subtitle: 'C-level decision support',
    description: 'Advanced executive intelligence platform with market intelligence aggregation, competitive analysis automation, and strategic decision support for C-level executives.',
    icon: Brain,
    image: 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Intelligence & Analytics',
    status: 'Available',
    features: [
      'Executive dashboards',
      'Market intelligence aggregation',
      'Competitive analysis automation',
      'Strategic planning tools',
      'Risk assessment models',
      'Board reporting automation',
      'Scenario planning',
      'Decision support AI'
    ],
    pricing: { setup: 115200, monthly: 5299 },
    color: 'from-indigo-500 to-purple-600',
    businessTypes: ['#executive-intelligence', '#strategic-planning', '#competitive-analysis', '#c-level-analytics', '#decision-support']
  },
  {
    id: 'mlops-platform',
    title: 'Advanced Machine Learning Operations (MLOps) Platform',
    subtitle: 'Model lifecycle management',
    description: 'Comprehensive MLOps platform with automated ML pipeline deployment, model lifecycle management, and performance monitoring for enterprise AI operations.',
    icon: Brain,
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Intelligence & Analytics',
    status: 'Available',
    features: [
      'Model lifecycle management',
      'Automated ML pipelines',
      'Performance monitoring',
      'Version control for models',
      'A/B testing framework',
      'Model governance',
      'Deployment automation',
      'Explainable AI tools'
    ],
    pricing: { setup: 89600, monthly: 4122 },
    color: 'from-purple-500 to-pink-600',
    businessTypes: ['#mlops', '#machine-learning', '#ai-operations', '#model-management', '#automated-ml']
  },
  {
    id: 'real-time-operational-intelligence',
    title: 'Real-Time Operational Intelligence Suite',
    subtitle: 'IoT data processing',
    description: 'Advanced operational intelligence platform with IoT data processing, edge analytics capabilities, and predictive maintenance algorithms for real-time operations.',
    icon: Eye,
    image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Intelligence & Analytics',
    status: 'Available',
    features: [
      'IoT data processing',
      'Edge analytics',
      'Predictive maintenance',
      'Real-time monitoring',
      'Anomaly detection',
      'Operational dashboards',
      'Alert management',
      'Historical analysis'
    ],
    pricing: { setup: 71680, monthly: 3297 },
    color: 'from-green-500 to-emerald-600',
    businessTypes: ['#iot-analytics', '#operational-intelligence', '#predictive-maintenance', '#edge-computing', '#real-time-analytics']
  },
  {
    id: 'strategic-business-intelligence',
    title: 'Strategic Business Intelligence Command Center',
    subtitle: 'Multi-dimensional data analysis',
    description: 'Enterprise strategic business intelligence platform with multi-dimensional analysis, advanced visualization, and automated insight generation for strategic decision making.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Intelligence & Analytics',
    status: 'Available',
    features: [
      'Multi-dimensional analysis',
      'Advanced data visualization',
      'Automated insight generation',
      'Strategic planning tools',
      'What-if scenario modeling',
      'Executive reporting',
      'Data storytelling',
      'Collaboration workspace'
    ],
    pricing: { setup: 81920, monthly: 3768 },
    color: 'from-blue-500 to-indigo-600',
    businessTypes: ['#strategic-intelligence', '#multi-dimensional-analysis', '#executive-analytics', '#data-storytelling', '#strategic-planning']
  },
  {
    id: 'voice-conversation-intelligence',
    title: 'Voice & Conversation Intelligence',
    subtitle: 'AI-powered voice analytics',
    description: 'Enterprise-grade voice and conversation intelligence platform with 98% accuracy in sentiment detection, real-time transcription, compliance monitoring, and advanced speech analytics for contact centres and customer service operations.',
    icon: Mic,
    image: 'https://images.unsplash.com/photo-1589254065878-42c9da997008?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Intelligence & Analytics',
    categories: ['Intelligence & Analytics', 'Communication', 'Customer Service'],
    status: 'Available',
    features: [
      'Real-time transcription with 98% accuracy',
      'AI-powered sentiment analysis',
      'Emotion detection and tracking',
      'Compliance and quality monitoring',
      'Automated call scoring',
      'Speaker identification',
      'Keyword and phrase spotting',
      'Multi-language support (40+ languages)',
      'Integration with telephony systems',
      'Custom alert triggers',
      'Performance coaching insights',
      'Regulatory compliance reporting'
    ],
    pricing: { setup: 56320, monthly: 2591 },
    color: 'from-violet-500 to-purple-600',
    businessTypes: ['#voice-analytics', '#conversation-intelligence', '#call-centre', '#speech-recognition', '#sentiment-analysis', '#compliance-monitoring', '#quality-assurance', '#contact-centre']
  },
  {
    id: 'real-time-analytics-dashboard',
    title: 'Real-Time Analytics Dashboard',
    subtitle: 'Live data streaming platform',
    description: 'High-performance real-time analytics dashboard with sub-50ms latency, processing 1M+ events per second, customisable widgets, and advanced visualisation capabilities for instant business intelligence and operational monitoring.',
    icon: Activity,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Intelligence & Analytics',
    categories: ['Intelligence & Analytics', 'Operations', 'Business Tools'],
    status: 'Available',
    features: [
      'Sub-50ms data latency',
      '1M+ events per second processing',
      'Customisable drag-and-drop widgets',
      'Real-time data streaming',
      'Advanced chart visualisations',
      'Live metric updates',
      'Multi-source data integration',
      'Automated anomaly detection',
      'Custom threshold alerts',
      'Mobile-responsive dashboards',
      'Export and sharing capabilities',
      'Role-based access control'
    ],
    pricing: { setup: 46080, monthly: 2120 },
    color: 'from-emerald-500 to-teal-600',
    businessTypes: ['#real-time-analytics', '#live-dashboards', '#data-streaming', '#business-intelligence', '#operational-monitoring', '#performance-tracking', '#metrics', '#kpi-dashboards']
  }
];
